import { Layer1ReadinessBlock, Layer1SectorRow } from '../../services/lossStructureApi';
import LossLayer1Shadow from './LossLayer1Shadow';

interface Props {
  data: Layer1ReadinessBlock;
}

export default function LossLayer1Readiness({ data }: Props) {
  const sector = data.sector_exposure;
  const slip = data.stop_loss_slippage;
  const hold = data.strategy_sell_min_hold;

  return (
    <section className="loss-section loss-section-wide">
      <div className="loss-section-heading">
        <div>
          <h3>Layer 1 출혈 차단 측정</h3>
          <p className="loss-subnote">
            주문 경로 변경 없이 섹터 편중, STOP_LOSS 체결 지연, 전략 SELL min_hold를 읽기 전용으로 본다.
          </p>
        </div>
        <span className={`loss-pill ${data.order_path_changed ? 'loss-pill-danger' : ''}`}>
          {data.order_path_changed ? '주문 영향 있음' : 'read-only'}
        </span>
      </div>

      <div className="loss-layer1-summary">
        <Metric label="Unknown 보유" value={`${sector.unknown_current_count}/${sector.current_position_count}`} />
        <Metric label="Unknown 거래" value={`${sector.unknown_trade_count}/${sector.trade_count}`} />
        <Metric label="STOP_LOSS 표본" value={`${slip.sample_count}건`} />
        <Metric label="기준보다 악화" value={`${slip.worse_than_baseline_count}건`} />
        <Metric label="평균 슬리피지" value={`${fmtPct(slip.avg_slippage_percent)}`} />
        <Metric label="전략 SELL min_hold 차단" value={`${hold.blocked_count}건`} />
      </div>

      <div className="loss-layer1-grid">
        <Table title="현재 보유 섹터" rows={sector.current} type="current" />
        <Table title="실현 손익 섹터" rows={sector.realized} type="realized" />
        <div className="loss-layer1-panel">
          <h4>STOP_LOSS 악화 사례</h4>
          <table className="loss-table">
            <thead>
              <tr><th>종목</th><th>수익률</th><th>차이</th><th>손익</th></tr>
            </thead>
            <tbody>
              {slip.worst_examples.length === 0 ? (
                <tr><td colSpan={4} className="loss-empty">데이터 없음</td></tr>
              ) : slip.worst_examples.map((row) => (
                <tr key={`${row.symbol}-${row.executed_at}`}>
                  <td>{row.symbol}</td>
                  <td className="loss-cell-bad">{fmtPct(row.profit_percent)}</td>
                  <td className={row.slippage_percent < 0 ? 'loss-cell-bad' : 'loss-cell-good'}>
                    {fmtPct(row.slippage_percent)}
                  </td>
                  <td>{fmtMoney(row.profit_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <LossLayer1Shadow evaluation={data.shadow_evaluation} />

      <ul className="loss-notes">
        {data.notes.map((note) => <li key={note}>{note}</li>)}
        <li>{hold.note}</li>
      </ul>
    </section>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return <div><span>{label}</span><strong>{value}</strong></div>;
}

function Table({ title, rows, type }: { title: string; rows: Layer1SectorRow[]; type: 'current' | 'realized' }) {
  return (
    <div className="loss-layer1-panel">
      <h4>{title}</h4>
      <table className="loss-table">
        <thead>
          <tr><th>섹터</th><th>{type === 'current' ? '보유' : '거래'}</th><th>{type === 'current' ? '종목' : '손익'}</th></tr>
        </thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={3} className="loss-empty">데이터 없음</td></tr> : rows.map((row) => (
            <tr key={row.sector} className={row.sector === 'Unknown' ? 'loss-row-muted' : ''}>
              <td>{row.sector}</td>
              <td>{type === 'current' ? row.position_count : row.trade_count}</td>
              <td>{type === 'current' ? (row.symbols || []).join(', ') : fmtMoney(row.realized_pl || 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function fmtPct(value: number) {
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
}

function fmtMoney(value: number) {
  return `${value < 0 ? '-' : ''}$${Math.abs(value).toLocaleString()}`;
}
